import { AuthResponseDTOAuthUserDTO, getAuthUserUrl } from "./sdk";

import { useAuthStore } from "@/common/lib";

// Store the original fetch
const originalFetch = window.fetch;

// Track if we're currently refreshing to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh the access token using the refresh token
 * Assumes endpoint: POST /api/user/refresh with refreshToken in body or header
 */
async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, saveAuth, clearAuth } = useAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    return null;
  }

  try {
    // Adjust this endpoint based on your actual API
    const response = await originalFetch(getAuthUserUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
      // Don't use the interceptor for refresh requests to avoid infinite loops
    });

    if (!response.ok) {
      throw new Error("Failed to refresh token");
    }

    const data = (await response.json()) as AuthResponseDTOAuthUserDTO;

    if (data.payload) {
      const newAccessToken = data.payload.accessToken;

      // skip refresh token because it is null
      saveAuth({
        id: data.payload.id,
        role: data.payload.role,
        accessToken: newAccessToken,
      });

      return newAccessToken;
    }

    throw new Error("No access token in refresh response");
  } catch (error) {
    console.error("Token refresh failed:", error);
    clearAuth();
    return null;
  }
}

/**
 * Override window.fetch to automatically add Authorization header and handle 401 responses
 */
window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // Get current access token from store
  const { accessToken } = useAuthStore.getState();

  // Prepare headers
  const headers = new Headers(init?.headers);

  // Add Authorization header if we have an access token and it's not already set
  // Skip for refresh endpoint to avoid infinite loops
  const url =
    typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  const isRefreshEndpoint = url.includes("/api/user/refresh");

  if (accessToken && !isRefreshEndpoint && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  // Make the original request
  const response = await originalFetch(input, {
    ...init,
    headers,
  });

  // Handle 401 Unauthorized responses
  if (response.status === 401 && !isRefreshEndpoint) {
    // If we're not already refreshing, start the refresh process
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken();
    }

    // Wait for refresh to complete (whether we started it or it was already in progress)
    const newToken = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;

    if (newToken) {
      // Retry the original request with the new token
      headers.set("Authorization", `Bearer ${newToken}`);
      return originalFetch(input, {
        ...init,
        headers,
      });
    }
  }

  return response;
};

// Export for potential cleanup or testing
export function restoreOriginalFetch() {
  window.fetch = originalFetch;
}
